import { selectedOrganisationAtom } from "@/atoms/store";
import LinkWithPersistentQuery from "@components/LinkWithQuery/LinkWithQuery";
import { Button } from "@tracify/ui/button";
import { ChevronLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { usePresets } from "@lib/api-hooks/usePresets/usePresets";
import { CONSTANT_LABELS } from "@/constants/constants";
import { useAtom } from "jotai";
import { useRouter } from "next/navigation";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useAttributionPresetState } from "@lib/hooks/url-state";
import { setLastAttributionPresetForOrg } from "@lib/util-functions/last-attribution-preset-storage";

type Props = {
  open?: boolean;
  channel: string;
};

const TouchpointNotInPresetModal = ({ open, channel }: Props) => {
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [selectedOrganisationId] = useAtom(selectedOrganisationAtom);
  const {
    attributionPreset: selectedPreset,
    setAttributionPreset: setSelectedPreset,
  } = useAttributionPresetState();
  const { data: presets } = usePresets(selectedOrganisationId ?? "", {
    enabled: Boolean(selectedOrganisationId),
  });

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const handleGoBack = useCallback(() => {
    const referrer = document.referrer
      ? new URL(document.referrer, window.location.href)
      : null;
    if (
      !referrer ||
      referrer.origin !== window.location.origin ||
      referrer.pathname.startsWith("/login")
    ) {
      router.push("/");
    } else {
      router.back();
    }
  }, [router]);

  const handleChangeToFirstPreset = useCallback(() => {
    const firstPreset = presets?.[0];

    if (firstPreset) {
      setSelectedPreset(firstPreset.presetId);
      if (selectedOrganisationId) {
        setLastAttributionPresetForOrg(
          selectedOrganisationId,
          firstPreset.presetId
        );
      }
    } else {
      toast.error(
        "Couldn't find any presets. Please go back and select one manually or contact support."
      );
    }
  }, [setSelectedPreset, presets, selectedOrganisationId]);

  const firstPresetName = presets?.[0]?.name || "First Preset";

  if (!mounted || !open) return null;

  return (
    <div className="fixed inset-0 z-popover">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      <div className="relative min-h-full flex items-center justify-center px-4 py-8 text-left lg:pl-64">
        <div className="relative w-full max-w-3xl p-6 sm:p-8 overflow-hidden transition-all transform bg-popover rounded-lg shadow-xl">
          <button
            onClick={handleGoBack}
            aria-label="Close panel"
            className="focus:outline-none absolute right-0 top-0 m-2"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          <h3 className="text-lg font-medium leading-6 mb-4 pr-6">
            {CONSTANT_LABELS[channel]} data is needed to view this dashboard
          </h3>

          <div className="flex flex-col gap-8">
            <p>
              You’ve selected a custom preset that does not include{" "}
              {CONSTANT_LABELS[channel]} data. You can go back or change to our
              standard preset.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <Button
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={handleGoBack}
              >
                Go Back
              </Button>
              <Button
                variant="primary"
                className="w-full sm:w-auto"
                onClick={handleChangeToFirstPreset}
              >
                Change to {firstPresetName}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TouchpointNotInPresetModal;
